import React, { useState } from "react";
import {
    LayoutGrid,
    FileText,
    GitBranch,
    FolderOpen,
    ChevronDown,
} from "lucide-react";
import {
    formatValue,
    isStatusKey,
    StatusBadge,
    isBooleanField,
} from "../lib/utils-fields";

const TAB_ICONS = {
    ozet: LayoutGrid,
    bilgiler: FileText,
    islem: GitBranch,
    evrak: FolderOpen,
};

export function SigortaSummaryHero({ row }) {
    if (!row) return null;
    const muvekkil =
        row.muvekkil_display ||
        row.muvekkil_ad_soyad ||
        row.muvekkil_label ||
        "—";
    const chips = [
        { label: "Ana durum", value: row.dosya_durumu_display || row.dosya_durumu },
        { label: "Alt durum", value: row.dosya_durumu_display || row.dosya_durumu },
        { label: "Plaka", value: row.plaka },
        { label: "Sigorta", value: row.sigorta_sirketi },
        { label: "Kaza", value: row.kaza_tarihi ? String(row.kaza_tarihi).slice(0, 10) : null },
        { label: "Kalan gün", value: row.kalan_gun != null ? `${row.kalan_gun} gün` : null },
    ].filter((c) => c.value);

    return (
        <section className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#1a2438] via-[#121A2F] to-[#0B0F19] p-5 mb-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">
                        Sigorta Dosyası
                    </div>
                    <div className="font-display text-2xl text-white">{row.dosya_no || `#${row.id}`}</div>
                    <div className="text-sm text-[#F3E5AB]/90 mt-1">{muvekkil}</div>
                    <div className="text-xs text-slate-400 mt-0.5">
                        {[row.marka, row.model, row.model_yili].filter(Boolean).join(" · ")}
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    {row.dosya_durumu && (
                        <StatusBadge value={row.dosya_durumu_display || row.dosya_durumu} />
                    )}
                    {row.dosya_durumu && (
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                            {row.dosya_durumu_display || row.dosya_durumu}
                        </span>
                    )}
                </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
                {chips.map((c) => (
                    <div
                        key={c.label}
                        className="rounded-lg border border-white/10 bg-black/20 px-3 py-2 min-w-[100px]"
                    >
                        <div className="text-[9px] uppercase tracking-wider text-slate-500">{c.label}</div>
                        <div className="text-sm text-slate-200 mt-0.5 truncate max-w-[140px]">{c.value}</div>
                    </div>
                ))}
            </div>
        </section>
    );
}

export function DetailTabBar({ tabs, active, onChange }) {
    return (
        <div className="flex flex-wrap gap-1 p-1 rounded-xl border border-white/10 bg-[#0B0F19]/80 mb-4">
            {tabs.map((tab) => {
                const Icon = TAB_ICONS[tab.id] || FileText;
                const isActive = active === tab.id;
                return (
                    <button
                        key={tab.id}
                        type="button"
                        onClick={() => onChange(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm transition ${
                            isActive
                                ? "bg-[#D4AF37]/20 text-[#F3E5AB] border border-[#D4AF37]/40"
                                : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
                        }`}
                    >
                        <Icon className="h-4 w-4 shrink-0" />
                        {tab.label}
                    </button>
                );
            })}
        </div>
    );
}

export function DetailFieldGrid({ fieldGroups, highlightKeys = new Set(), compact = false }) {
    const [openGroups, setOpenGroups] = useState(() =>
        Object.fromEntries(fieldGroups.map(([g], i) => [g, i < 2])),
    );

    if (!fieldGroups.length) {
        return <p className="text-sm text-slate-500 py-8 text-center">Gösterilecek alan yok.</p>;
    }

    return (
        <div className="space-y-3">
            {fieldGroups.map(([groupTitle, fields]) => {
                const isOpen = openGroups[groupTitle] !== false;
                return (
                    <section
                        key={groupTitle}
                        className="rounded-xl border border-white/10 bg-[#121A2F]/70 overflow-hidden"
                    >
                        <button
                            type="button"
                            onClick={() =>
                                setOpenGroups((s) => ({ ...s, [groupTitle]: !isOpen }))
                            }
                            className="w-full px-4 py-3 flex items-center gap-2 text-left hover:bg-white/[0.03] transition"
                        >
                            <FileText className="h-3.5 w-3.5 text-[#D4AF37]/80 shrink-0" />
                            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">
                                {groupTitle}
                            </span>
                            <span className="text-[10px] text-slate-600 ml-1">({fields.length})</span>
                            <ChevronDown
                                className={`h-4 w-4 ml-auto text-slate-500 transition ${
                                    isOpen ? "" : "-rotate-90"
                                }`}
                            />
                        </button>
                        {isOpen && (
                            <dl
                                className={`px-4 pb-4 grid gap-0 divide-y divide-white/5 ${
                                    compact
                                        ? "grid-cols-1"
                                        : "grid-cols-1 lg:grid-cols-2"
                                }`}
                            >
                                {fields.map((f) => (
                                    <DetailRow
                                        key={f.name}
                                        field={f}
                                        highlight={highlightKeys.has(f.name)}
                                    />
                                ))}
                            </dl>
                        )}
                    </section>
                );
            })}
        </div>
    );
}

function DetailRow({ field, highlight }) {
    const { label, value, name, type } = field;
    return (
        <div
            className={`grid grid-cols-[minmax(120px,38%)_1fr] gap-3 py-2.5 ${
                highlight ? "bg-[#D4AF37]/5 -mx-4 px-4" : ""
            }`}
        >
            <dt className="text-[11px] text-slate-500 leading-snug">{label}</dt>
            <dd className="text-sm text-slate-100 break-words">
                {highlight ? (
                    <span className="text-[#F3E5AB] font-medium">{formatField(value, name, type)}</span>
                ) : (
                    formatField(value, name, type)
                )}
            </dd>
        </div>
    );
}

function formatField(value, name, type) {
    if (value === null || value === undefined || value === "") return "—";
    if (isStatusKey(name) && !isBooleanField(name, type)) {
        return <StatusBadge value={value} />;
    }
    return formatValue(value, type, name);
}
